<!--@format-->
<template>
    <div class="assign-user-company-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Assign Users</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="loading" class="loading-card">
                    <loading-screen-svg />
                </div>
                <template v-else>
                    <p class="content-title">
                        Select the users that you would like to assign to this company.
                    </p>
                    <p class="content-description">
                        Assigning a user will help keep track of what companies they're working
                        with.
                    </p>
                    <div class="input-container">
                        <agency-users-search
                            :options="getOptions()"
                            :selected="assignedUsers"
                            @assign="assignUser($event)"
                            @remove="removeUser($event)"
                        />
                    </div>
                </template>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :disabled="isSaving" @click="save()">
                        {{ isSaving ? 'Saving...' : 'Save' }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AgencyUsersSearch = () => import('@/components/client/agency-users-search.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { differenceBy } from 'lodash';

export default {
    name: 'assign-user-company-widget',

    components: {
        'agency-users-search': AgencyUsersSearch,
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            isSaving: false,
            companyId: this.widget.options.companyId,
            availableUsers: [],
            assignedUsers: [],
        };
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },
    },

    created() {
        this.getData();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getOptions() {
            return differenceBy(this.availableUsers, this.assignedUsers, 'id');
        },

        getData() {
            this.loading = true;
            Promise.all([this.getCompanyAssignedUsers(), this.getUsers()]).finally(() => {
                this.loading = false;
            });
        },

        save() {
            return new Promise((resolve, reject) => {
                this.isSaving = true;
                const changes = [
                    {
                        name: 'Users',
                        value: this.assignedUsers.map(({ id }) => ({ UserId: id })),
                    },
                ];
                this.$api
                    .patch(`/api/Companies/${this.companyId}`, changes)
                    .then(() => {
                        this.$helpers.showSuccessNotification('Your changes have been saved.');
                        EventBus.$emit('company:update');
                        resolve();
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to save your company', {
                            error,
                        });
                        reject();
                    });
            });
        },

        getCompanyAssignedUsers() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Companies?$filter=Id eq ${this.companyId}&$select=Id&$expand=CompanyUsers($expand=User($select=Id,FirstName,LastName,ProfileImageUrl,Title))`,
                    )
                    .then(({ data }) => {
                        const [companyData] = data.value;
                        this.assignedUsers = companyData.CompanyUsers.map(({ User }) => {
                            return this.mapClientUserToUserOption(User);
                        });
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching assigned users.',
                            {
                                error,
                            },
                        );
                        reject();
                    });
            });
        },

        getUsers() {
            return new Promise((resolve, reject) => {
                const filter = `$filter=ClientId eq ${this.clientId} and IsActive eq true&$select=Id,FirstName,LastName,ProfileImageUrl,Title`;
                const query = `/service/Users?${filter}`;
                this.$api
                    .get(query)
                    .then(({ data }) => {
                        this.availableUsers = data.value.map((user) =>
                            this.mapClientUserToUserOption(user),
                        );
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching users.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        mapClientUserToUserOption(user) {
            return {
                id: user.Id,
                name: `${user.FirstName} ${user.LastName}`,
                profilePicture: user.ProfileImageUrl,
                title: user.Title,
            };
        },

        assignUser(userToAssign) {
            this.assignedUsers.push(userToAssign);
        },

        removeUser(userToRemove) {
            this.assignedUsers = this.assignedUsers.filter(({ id }) => id !== userToRemove.id);
        },
    },
};
</script>

<style>
.assign-user-company-widget .widget-contents-container .content-wrapper {
    padding: 28px 36px;
}

.assign-user-company-widget .widget-contents-container .content-wrapper .content-title {
    margin-bottom: 4px;
    font: bold 18px/24px 'Nunito';
}

.assign-user-company-widget .widget-contents-container .content-wrapper .content-description {
    margin-bottom: 24px;
    font: 400 16px/24px 'Roboto';
}

.assign-user-company-widget .widget-contents-container .content-wrapper .loading-card {
    max-height: 100%;
    position: relative;
}

.assign-user-company-widget
    .widget-contents-container
    .content-wrapper
    .loading-card
    .loading-screen {
    text-align: center;
}

.assign-user-company-widget
    .widget-contents-container
    .content-wrapper
    .loading-card
    .loading-screen {
    margin: 0 !important;
}

.assign-user-company-widget .widget-contents-container .content-wrapper .input-container {
    margin: 1.5em 0;
}

.assign-user-company-widget .widget-contents-container .content-wrapper .assigned-users {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
}

.assign-user-company-widget
    .widget-contents-container
    .content-wrapper
    .assigned-users
    .user-chips {
    align-items: center;
    border: 1px solid #cac6cb;
    border-radius: 15px;
    display: flex;
    font-size: 12px;
    gap: 8px;
    padding: 3px 10px;
}

.assign-user-company-widget
    .widget-contents-container
    .content-wrapper
    .assigned-users
    .user-chips
    img {
    border-radius: 50%;
    height: 20px;
    width: 20px;
}

.assign-user-company-widget
    .widget-contents-container
    .content-wrapper
    .assigned-users
    .user-chips
    svg {
    cursor: pointer;
    margin-left: 12px;
    width: 10px;
}

.assign-user-company-widget .cancel-btn {
    background: transparent;
    border: none;
    color: #877d88;
}

.assign-user-company-widget .cancel-btn:hover {
    background: #e9e9ef;
}
</style>
